import { Skeleton } from '@apps/web/components/ui/skeleton';
import { cn } from '@apps/web/lib/utils';
import { type StatusTile } from '@libraries/typescript/api_client';

type StatusHeroProps = {
    // undefined while the status section is still loading — renders a skeleton
    // row so the hero never collapses between fetches.
    readonly tiles: Array<StatusTile> | undefined;
    // Passes the whole tile so the route can open the matching evidence dialog
    // (keyed on tile.key) and still fall back to tile.target_tab navigation.
    readonly onTileSelect: (tile: StatusTile) => void;
};

// The card tint is reserved for the amber/red severity states; ok stays neutral
// and leans on the green dot, so severity color never appears for a healthy
// tile. The headline text also names the state, so the level is legible without
// relying on color (accessibility).
const LEVEL_CARD_CLASSES: Record<StatusTile['level'], string> = {
    ok: 'bg-muted/30',
    amber: 'border-amber-500/60 bg-amber-500/10',
    red: 'border-red-500/60 bg-red-500/10',
};

const LEVEL_DOT_CLASSES: Record<StatusTile['level'], string> = {
    ok: 'bg-emerald-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
};

const LEVEL_HEADLINE_CLASSES: Record<StatusTile['level'], string> = {
    ok: 'text-foreground',
    amber: 'text-amber-600 dark:text-amber-400',
    red: 'text-red-600 dark:text-red-400',
};

// Short display labels so tiles don't clip mid-word at narrow widths;
// unknown keys fall back to humanizing the key itself.
const TILE_LABELS: Record<string, string> = {
    stuck_runs: 'Stuck runs',
    stale: 'Stale',
    tool_errors: 'Tool errors',
    zero_suggestions: '0-suggestion',
    judge_flags: 'Judge flags',
    cost_pace: 'Cost pace',
};

function humanizeKey(key: string): string {
    const label = TILE_LABELS[key];
    if (label) {
        return label;
    }
    const words = key.split('_').join(' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
}

function StatusHeroTile({
    tile,
    onSelect,
}: {
    readonly tile: StatusTile;
    readonly onSelect: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onSelect}
            title={tile.detail ?? undefined}
            className={cn(
                'hover:border-ring focus-visible:border-ring focus-visible:ring-ring/50 flex h-full cursor-pointer flex-col gap-1 rounded-lg border p-3 text-left transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-none',
                LEVEL_CARD_CLASSES[tile.level],
            )}
        >
            <span className="flex items-center gap-1.5">
                <span
                    className={cn(
                        'inline-block h-2 w-2 shrink-0 rounded-full',
                        LEVEL_DOT_CLASSES[tile.level],
                    )}
                />
                <span className="text-muted-foreground truncate text-xs font-medium tracking-wide uppercase">
                    {humanizeKey(tile.key)}
                </span>
            </span>
            <span
                className={cn(
                    'text-sm font-semibold',
                    LEVEL_HEADLINE_CLASSES[tile.level],
                )}
            >
                {tile.headline}
            </span>
            {tile.detail ? (
                <span className="text-muted-foreground text-xs">
                    {tile.detail}
                </span>
            ) : null}
        </button>
    );
}

export function StatusHero({ tiles, onTileSelect }: StatusHeroProps) {
    if (tiles === undefined) {
        return (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                    <Skeleton key={index} className="h-20 w-full" />
                ))}
            </div>
        );
    }
    return (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {tiles.map((tile) => (
                <StatusHeroTile
                    key={tile.key}
                    tile={tile}
                    onSelect={() => onTileSelect(tile)}
                />
            ))}
        </div>
    );
}
